import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, Trash2, GripVertical, Image as ImageIcon } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ProposalImage {
  id: string;
  image_url: string;
  title: string;
  caption: string | null;
  display_order: number;
  is_active: boolean;
}

export function ProposalGalleryManager() {
  const [images, setImages] = useState<ProposalImage[]>([]);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    const { data, error } = await supabase
      .from('proposal_gallery')
      .select('*')
      .order('display_order');

    if (error) {
      toast({
        title: "Error loading images",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    setImages(data || []);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);

    for (const file of Array.from(files)) {
      try {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `proposal-gallery/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('product-images')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('product-images')
          .getPublicUrl(filePath);

        const maxOrder = Math.max(0, ...images.map(img => img.display_order));

        const { error: insertError } = await supabase
          .from('proposal_gallery')
          .insert({
            image_url: publicUrl,
            title: file.name.replace(/\.[^/.]+$/, ""),
            display_order: maxOrder + 1,
            is_active: true,
          });

        if (insertError) throw insertError;
      } catch (error: any) {
        toast({
          title: "Upload failed",
          description: error.message,
          variant: "destructive",
        });
      }
    }

    setUploading(false);
    loadImages();
    toast({
      title: "Images uploaded",
      description: "Successfully uploaded images to proposal gallery",
    });
  };

  const handleUpdate = async (id: string, updates: Partial<ProposalImage>) => {
    const { error } = await supabase
      .from('proposal_gallery')
      .update(updates)
      .eq('id', id);

    if (error) {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    setImages(images.map(img => img.id === id ? { ...img, ...updates } : img));
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    const { error } = await supabase
      .from('proposal_gallery')
      .delete()
      .eq('id', deleteId);

    if (error) {
      toast({
        title: "Delete failed",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    setImages(images.filter(img => img.id !== deleteId));
    setDeleteId(null);
    toast({
      title: "Image deleted",
      description: "Image removed from proposal gallery",
    });
  };

  const handleReorder = async (id: string, direction: 'up' | 'down') => {
    const index = images.findIndex(img => img.id === id);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === images.length - 1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const newImages = [...images];
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];

    // Update display_order for both items
    await Promise.all([
      supabase.from('proposal_gallery').update({ display_order: newIndex }).eq('id', newImages[newIndex].id),
      supabase.from('proposal_gallery').update({ display_order: index }).eq('id', newImages[index].id),
    ]);

    setImages(newImages);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Upload Images</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Input
              type="file"
              accept="image/*"
              multiple
              onChange={handleUpload}
              disabled={uploading}
              className="flex-1"
            />
            <Button disabled={uploading}>
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4">
        {images.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No images in proposal gallery yet</p>
            </CardContent>
          </Card>
        ) : (
          images.map((image, index) => (
            <Card key={image.id}>
              <CardContent className="p-4">
                <div className="flex gap-4">
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleReorder(image.id, 'up')}
                      disabled={index === 0}
                    >
                      <GripVertical className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleReorder(image.id, 'down')}
                      disabled={index === images.length - 1}
                    >
                      <GripVertical className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="w-32 h-24 rounded overflow-hidden bg-muted flex-shrink-0">
                    <img
                      src={image.image_url}
                      alt={image.title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="flex-1 space-y-3">
                    <div>
                      <Label htmlFor={`title-${image.id}`}>Title</Label>
                      <Input
                        id={`title-${image.id}`}
                        value={image.title}
                        onChange={(e) => handleUpdate(image.id, { title: e.target.value })}
                        onBlur={() => loadImages()}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`caption-${image.id}`}>Caption</Label>
                      <Textarea
                        id={`caption-${image.id}`}
                        value={image.caption || ""}
                        onChange={(e) => handleUpdate(image.id, { caption: e.target.value })}
                        onBlur={() => loadImages()}
                        rows={2}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 items-end">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`active-${image.id}`}>Active</Label>
                      <Switch
                        id={`active-${image.id}`}
                        checked={image.is_active}
                        onCheckedChange={(checked) => handleUpdate(image.id, { is_active: checked })}
                      />
                    </div>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => setDeleteId(image.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Image?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this image from the proposal gallery. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
